import React from "react";
import { Divider, Stack } from "@mui/material";
import { HashLink } from "react-router-hash-link";
import ChipRender from "./ChipRender";
import { checkSegmentStatus } from "./AppraisalFunctions";
import AppraisalStatusIcon from "./AppraisalStatusIcon";
import AppraisalFormStatusIcon from "./AppraisalFormStatusIcon";

const RenderSegments = ({
    segments,
    status,
    isAppraiser,
    appraisalId,
    type = "inputs",
}) =>
    segments.map((step) => {
        const clickable = type !== "outputs" || !!status;
        const chip = (
            <ChipRender
                label={step.label ?? step.title}
                status={checkSegmentStatus(step, status, isAppraiser)}
                key={step.index}
                clickable={clickable}
            />
        );
        if (!clickable) {
            return chip;
        }

        return (
            <HashLink
                key={step.index}
                to={`/rms/appraisals/${appraisalId}#io-segment-${type}-${step.index}`}
                scroll={(el) => {
                    const yCoordinate =
                        el.getBoundingClientRect().top + window.scrollY;
                    const yOffset = -80;
                    window.scrollTo({
                        top: yCoordinate + yOffset,
                        behavior: "smooth",
                    });
                }}
            >
                {chip}
            </HashLink>
        );
    });

const AppraisalProgressSummary = ({
    inputsStatus,
    meetingStatus,
    outputsStatus,
    surveyStatus,
    appraisalStatus,
    isAppraiser,
}) => (
    <Stack direction="row" justifyContent="flex-start" spacing={0}>
        <AppraisalFormStatusIcon
            status={inputsStatus}
            form="inputs"
            isAppraiser={isAppraiser}
        />
        <AppraisalStatusIcon status={meetingStatus} section="meeting" />
        <AppraisalFormStatusIcon
            status={outputsStatus}
            form="outputs"
            isAppraiser={isAppraiser}
        />
        <AppraisalStatusIcon status={surveyStatus} section="survey" />
        <AppraisalStatusIcon status={appraisalStatus} section="appraisal" />
    </Stack>
);

function AppraisalProgressDetails({
    inputs = [],
    outputs = [],
    survey,
    meeting,
    status = "open",
    display = "summary",
    isAppraiser = false,
    appraisalId,
}) {
    const inputsStatus = inputs?.status ?? "";
    const inputsSegments = inputs?.segments?.length ? inputs.segments : [];

    const outputsStatus = outputs?.status ?? "";
    const outputsSegments = outputs?.segments?.length ? outputs.segments : [];

    if (
        display === "summary" ||
        inputsSegments.length + outputsSegments.length < 1
    ) {
        return (
            <AppraisalProgressSummary
                inputsStatus={inputsStatus}
                meetingStatus={meeting?.status}
                outputsStatus={outputsStatus}
                surveyStatus={survey?.status}
                appraisalStatus={status}
                isAppraiser={isAppraiser}
            />
        );
    }

    return (
        <>
            {!!inputsSegments.length && (
                <>
                    <Divider textAlign="left">Inputs</Divider>
                    <RenderSegments
                        segments={inputsSegments}
                        status={inputsStatus}
                        isAppraiser={isAppraiser}
                        appraisalId={appraisalId}
                        type="inputs"
                    />
                </>
            )}

            {!!outputsSegments.length && (
                <>
                    <Divider textAlign="left">Outputs</Divider>
                    <RenderSegments
                        segments={outputsSegments}
                        status={outputsStatus}
                        isAppraiser={isAppraiser}
                        appraisalId={appraisalId}
                        type="outputs"
                    />
                </>
            )}
        </>
    );
}

export default React.memo(AppraisalProgressDetails);
